import React from 'react';
import { Navigate } from 'react-router-dom';
import { Permission } from '../constants/permissions';
import UserRole from '../constants/user-role';
import useFeatures, { FeatureType } from '../hooks/features';
import useAuthContext from '../hooks/useAuthContext';
import { hasPermission, hasRole, hasAnyPermission, hasAllPermissions } from '../utils/permissionUtils';

export interface ProtectedRouteProps {
  children: React.ReactElement;

  // Permission-based guard (recommended approach)
  permission?: Permission;
  anyPermission?: Permission[];
  allPermissions?: Permission[];

  // Legacy role-based guard (for backwards compatibility)
  roles?: UserRole | UserRole[];

  // Feature flag guard
  feature?: FeatureType;

  // Redirect paths
  redirectTo?: string;
  unauthorizedRedirect?: string;

  // Custom validation function
  customGuard?: (authState: ReturnType<typeof useAuthContext>['state']) => boolean;

  // Require authentication (default: true)
  requireAuth?: boolean;

  // Guest-only guard: redirects already-authenticated users away (e.g. login/forgot-password pages)
  requireGuest?: boolean;
}

/**
 * ProtectedRoute Component
 *
 * A flexible route guard that can check:
 * - Authentication status
 * - User permissions (combines roles + features)
 * - Specific roles
 * - Feature flags
 * - Custom validation logic
 *
 * @example
 * // Using permission (recommended)
 * <ProtectedRoute permission={Permission.VIEW_VET_DASHBOARD}>
 *   <VeterinaryDashboard />
 * </ProtectedRoute>
 *
 * @example
 * // Using roles (legacy)
 * <ProtectedRoute roles={[UserRole.admin, UserRole.manager]}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Using feature flag
 * <ProtectedRoute feature={FeatureType.USE_COMPARE_FUNCTIONALITY}>
 *   <CompareView />
 * </ProtectedRoute>
 *
 * @example
 * // Using custom guard
 * <ProtectedRoute customGuard={(state) => state.farmId !== ''}>
 *   <FarmSpecificView />
 * </ProtectedRoute>
 *
 * @example
 * // Combining multiple guards
 * <ProtectedRoute
 *   permission={Permission.VIEW_SLAUGHTER_PREPARATION}
 *   customGuard={(state) => state.selectedBusinessUnitId !== ''}
 * >
 *   <SlaughterPreparation />
 * </ProtectedRoute>
 *
 * @example
 * // Guest-only page (login, forgot-password, accept-invitation, ...): redirect away if already authenticated
 * <ProtectedRoute requireGuest>
 *   <ForgotPassword />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  anyPermission,
  allPermissions,
  roles,
  feature,
  redirectTo = '/dashboard',
  unauthorizedRedirect,
  customGuard,
  requireAuth = true,
  requireGuest = false
}) => {
  const { state } = useAuthContext();
  const { hasFeature, isInitialized: featuresInitialized } = useFeatures();

  // Guest-only routes: authenticated users are redirected away, no other checks apply
  if (requireGuest) {
    return state.isLoggedIn ? <Navigate to={redirectTo} replace /> : children;
  }

  // Check authentication
  if (requireAuth && !state.isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // Check custom guard first (highest priority)
  if (customGuard && !customGuard(state)) {
    return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
  }

  // Check permission-based access
  if (permission && !hasPermission(state.role, permission)) {
    return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
  }

  // Check if user has any of the specified permissions
  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(state.role, anyPermission)) {
    return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
  }

  // Check if user has all of the specified permissions
  if (allPermissions && allPermissions.length > 0 && !hasAllPermissions(state.role, allPermissions)) {
    return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
  }

  // Check role-based access (legacy support)
  if (roles && !hasRole(state.role, roles)) {
    return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
  }

  // Check feature flag. The flags are fetched once, asynchronously, so deciding before they arrive would
  // bounce a deep link into an enabled page - wait for the fetch to settle instead.
  if (feature) {
    if (!featuresInitialized) {
      return null;
    }

    if (!hasFeature(feature)) {
      return <Navigate to={unauthorizedRedirect || redirectTo} replace />;
    }
  }

  // All checks passed, render the protected component
  return children;
};

export default ProtectedRoute;
